import formatCurrency from 'format-currency';
import _ from 'lodash';
import moment from 'moment';
import { dateFormat, timeFormatWithSeconds, TimeReferenceType, RequiredGtdTypeEnum } from '../../components/shared/constants';
import { currencyFormat } from '../../components/shared/utils'
import { Currencies } from '../../components/shared/currencies'
import { convertUtcTimeToHotelLocal } from '../../components/shared/timezoneUtils'



export const getDepositPolicyDescription = (policy, reservation, total) => {
    const timeReference = getTimeReferenceDateText(policy, reservation);
    const minAmount = getMinAmountText(policy, total)

    return window.optima.resourceManager.messages.ReservationDepositPolicyDescription
        .replace("{0}", minAmount)
        .replace("{1}", timeReference)
}

export const getCancellationPolicyDescription = (policy, total) => {
    const days = policy?.Days;
    const timeReference = getTimeReferenceText(policy?.TimeReference);
    const minAmount = getMinAmountText(policy, total)

    return window.optima.resourceManager.messages.ReservationCancellationPolicyDescription
        .replace("{0}", days)
        .replace("{1}", timeReference)
        .replace("{2}", minAmount)
}

export const getFreeCancellationDate = (reservation, cancellationPolicies) => {
    if(!cancellationPolicies?.length){
        return null
    }
    const afterReservationPolicy = findMaxPolicyByDays(cancellationPolicies, TimeReferenceType.AfterReservationDate);
    const beforeCheckInPolicy = findMaxPolicyByDays(cancellationPolicies, TimeReferenceType.BeforeArrivalDate);

    const afterReservationDate = getPolicyDate(afterReservationPolicy, reservation.CreateDate, 'add');
    const beforeCheckInDate = getPolicyDate(beforeCheckInPolicy, reservation.CheckInDate, 'subtract');

    if (afterReservationDate && beforeCheckInDate) {
        return moment.min(afterReservationDate, beforeCheckInDate);
    }
    return afterReservationDate || beforeCheckInDate; 
}

const getTotalPayed = (total) => {
    return Math.abs(total?.TotalAmountCreditAfterTax ?? 0);
}

export const getTotalPayedText = (total, currencyId) => {
    const totalPayed = getTotalPayed(total)
    return formatCurrency(
        totalPayed,
        currencyFormat(currencyId)
    )
}

export const getUpcomingCancellationFee = (cancellationPolicies, reservation, total) => {
    if(!cancellationPolicies?.length){
        return "";
    }

    const nearestPolicy = findNearestPolicyByDate(cancellationPolicies, reservation);

    return getMinAmountText(nearestPolicy, total)
}

const getTimeReferenceDateText = (policy, reservation) => {
    const referenceDate = policy?.TimeReference === TimeReferenceType.AfterReservationDate
        ? reservation.CreateDate
        : reservation.CheckInDate;

    const operation = policy?.TimeReference === TimeReferenceType.AfterReservationDate
        ? 'add'
        : 'subtract';

    return getPolicyDate(policy, referenceDate, operation)?.format(dateFormat);
}

const getPolicyDate = (policy, referenceDate, operation) => {
    if (!policy) return null;

    let date = moment(referenceDate);

    if (operation === 'add') {
        date = moment(referenceDate).add(policy.Days, 'days');
    } else {
        date = moment(referenceDate).subtract(policy.Days, 'days');
    }

    if (policy?.CancellationTime) {
        const cancellationTime = moment(policy.CancellationTime, timeFormatWithSeconds);

        const hours = cancellationTime.hour();
        const minutes = cancellationTime.minute();

        date = date.set({
            hour: hours,
            minute: minutes,
            second: 0,
            millisecond: 0
        });
    } else {
        // normalize even when there's no CancellationTime
        date = date.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    }

    return date;
};

const getMinAmountText = (policy, total) => {
    const minAmount = calculateMinimumAmount(total, policy);

    return formatCurrency(
        minAmount,
        currencyFormat(policy?.CurrencyId)
    )
}

const calculateMinimumAmount = (total, policy) => {
    const percentageOfTotal = (total?.TotalAmountDebitAfterTax ?? 0) * ((policy?.Percent ?? 0) / 100);
    const fixedAmount = policy?.Amount;
    const useLowerCostOption = policy?.ApplyLowestCostOption;

    const isPercentageValid = !_.isNaN(percentageOfTotal) && percentageOfTotal > 0;

    let minimumAmount = fixedAmount;

    if (useLowerCostOption && isPercentageValid) {
        minimumAmount = Math.min(percentageOfTotal, fixedAmount);
    } else if (isPercentageValid) {
        minimumAmount = percentageOfTotal;
    }

    return minimumAmount;
};

const getTimeReferenceText = (timeReference) =>
    timeReference === TimeReferenceType.AfterReservationDate
        ? window.optima.resourceManager.messages.AfterCreatedReservation.toLowerCase()
        : window.optima.resourceManager.messages.BeforeArrival.toLowerCase()

const findMaxPolicyByDays = (policies, timeReferenceType) => {
    const filtered = _(policies).filter(cp => cp.TimeReference === timeReferenceType).value();
    if (!filtered.length) return undefined;
    
    // Sort by Days descending, then by CancellationTime descending as tiebreaker
    // This ensures consistent results when multiple policies have the same Days value
    const sorted = _.orderBy(filtered, [
        'Days',
        p => {
            if (!p.CancellationTime) return 0;
            const time = moment(p.CancellationTime, timeFormatWithSeconds);
            return time.hour() * 60 + time.minute();
        }
    ], ['desc', 'desc']);
    
    return sorted[0];
};

const findNearestPolicyByDate = (policies, reservation) => {
    const currentDate = new Date();

    const mapped = policies.map(policy => {
        const referenceDate = policy?.TimeReference === TimeReferenceType.AfterReservationDate
            ? reservation.CreateDate
            : reservation.CheckInDate;

        const operation = policy?.TimeReference === TimeReferenceType.AfterReservationDate
            ? 'add'
            : 'subtract';

        const date = getPolicyDate(policy, referenceDate, operation).toDate();

        return { policy, date };
    });

    const valid = mapped.filter(x => x.date <= currentDate);

    if (valid.length > 0) {
        // Sort by date descending, then by Days descending, then by CancellationTime descending as tiebreakers
        // This ensures consistent results when multiple policies have the same date
        const sorted = _.orderBy(valid, [
            x => x.date.getTime(),
            x => x.policy.Days,
            x => {
                if (!x.policy.CancellationTime) return 0;
                const time = moment(x.policy.CancellationTime, timeFormatWithSeconds);
                return time.hour() * 60 + time.minute();
            }
        ], ['desc', 'desc', 'desc']);
        return sorted[0]?.policy;
    }
    return null;
};

const getAutoDescriptionAmountText = (policy, message) => {
    const currencySymbol = Currencies[policy?.CurrencyId]?.symbol ?? '';
    const amount = `${policy?.Amount ?? 0}${currencySymbol}`;
    const percentText = message('PercentPolicyAutomaticDescription')
        .replace('{0}', policy?.Percent ?? 0);

    if (policy?.ApplyLowestCostOption) {
        return message('LowestCostPolicyAutomaticDescription')
            .replace('{0}', amount)
            .replace('{1}', percentText);
    }
    if (policy?.Amount > 0) return amount;
    if (policy?.Percent > 0) return percentText;
    return '';
};

const getAutoDescriptionTimeReference = (timeReference, message, text) => {
    return timeReference === TimeReferenceType.BeforeArrivalDate
        ? text('BeforeArrivalDate').toLowerCase()
        : message('AfterCreatedReservation').toLowerCase();
};

const buildCancellationDescription = (policy, message, text) => {
    const amountText = getAutoDescriptionAmountText(policy, message);
    const timeRef = getAutoDescriptionTimeReference(policy?.TimeReference, message, text);
    const cancellationTime = policy?.CancellationTime
        ? moment(policy.CancellationTime, 'HH:mm:ss').format('HH:mm')
        : '00:00';

    return message('BaseCancellationPolicyAutomaticDescription')
        .replace('{0}', policy?.Days ?? 0)
        .replace('{1}', timeRef)
        .replace('{2}', cancellationTime)
        .replace('{3}', amountText);
};

const buildDepositDescription = (policy, message, text) => {
    const amountText = getAutoDescriptionAmountText(policy, message);
    const timeRef = getAutoDescriptionTimeReference(policy?.TimeReference, message, text);

    return message('BaseDepositPolicyAutomaticDescription')
        .replace('{0}', amountText)
        .replace('{1}', policy?.Days ?? 0)
        .replace('{2}', timeRef);
};

const buildGtdDescription = (requiredGtdType, gtdUtcTime, message, text, hotelTimezone, referenceDate) => {
    if (requiredGtdType == null || requiredGtdType === RequiredGtdTypeEnum.NoGuaranteeRequired) {
        return null;
    }
    const gtdTypeLabel = requiredGtdType === RequiredGtdTypeEnum.CreditCardRequired
        ? text('GuaranteeDescCcGuaranteed')
        : text('GuaranteeDescVoucherGuaranteed');

    let desc = message('GuaranteeDescRequires') + ' ' + gtdTypeLabel + '.';

    if (gtdUtcTime) {
        const localTime = convertUtcTimeToHotelLocal(gtdUtcTime, hotelTimezone, referenceDate);
        if (localTime) {
            desc += ' ' + message('GuaranteeDescValidUntil') + ' ' + localTime + ' ' + text('GuaranteeDescOnArrivalDate') + '.';
        }
    }
    return desc;
};

export const buildAutoDescription = (cancellationPolicies, depositPolicies, message, text, gtdInfo) => {
    const lines = [];

    if (cancellationPolicies?.length) {
        for (const cp of cancellationPolicies) {
            lines.push(buildCancellationDescription(cp, message, text));
        }
    }
    if (depositPolicies?.length) {
        for (const dp of depositPolicies) {
            lines.push(buildDepositDescription(dp, message, text));
        }
    }

    if (gtdInfo) {
        const gtdLine = buildGtdDescription(
            gtdInfo.requiredGtdType, gtdInfo.gtdUtcTime, message, text, gtdInfo.hotelTimezone, gtdInfo.referenceDate
        );
        if (gtdLine) {
            lines.push(gtdLine);
        }
    }

    return lines.join('\n');
};

export const buildAutoDescriptionForPolicy = (policy, message, text, hotelTimezone) => {
    const gtdInfo = policy?.RequiredGtdType != null ? {
        requiredGtdType: policy.RequiredGtdType,
        gtdUtcTime: policy.GtdUtcTime,
        hotelTimezone,
    } : null;

    return buildAutoDescription(
        policy?.CancellationPolicies,
        policy?.DepositPolicies,
        message,
        text,
        gtdInfo
    );
};
